// @ts-strict-ignore
import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  Application,
  ApplicationPageState as State,
  CaseWork,
  EntityDocumentsByDocToken,
  EntityNotes,
  EntityPrimaryData,
  EntitySecondaryData,
  Evaluation,
  PrimaryApplicationData,
  SecondaryApplicationData,
  SelectedJourneyApplicationStep,
} from './types';
import { initialState } from './state';

const mergeEntities = (currentEntities, newEntities) => {
  const updatedEntities = currentEntities.reduce((arr, entity) => {
    const newEntity = newEntities
      .find(e => e.id === entity.id || e.entityToken === entity.entityToken);
    const mergedEntity = {
      ...entity,
      ...newEntity,
    };

    newEntities.splice(newEntities.indexOf(newEntity), 1);
    return [...arr, mergedEntity];
  }, []);

  updatedEntities.push(...newEntities);
  return updatedEntities;
};

const mergeNotes = (currentNotes, newNotes) => {
  if (!newNotes.length) return currentNotes;

  return [...currentNotes, ...newNotes]
    .filter((note, index, self) => index === self.findIndex(n => n.note_id === note.note_id
        || (n.text === note.text && n.created_at === note.created_at),
    ),
    );
};

export default {
  mergeApplicationData: (
    state: State,
    { payload: application }: PayloadAction<Application>,
  ) => ({
    ...state,
    application,
  }),
  mergePrimaryApplicationData: (
    state: State,
    {
      payload: {
        application,
        applicationJourneyVersion,
        documentTypes,
        entities,
        outcomeCustomizations,
        reviewOutcomeObjects,
        reviewReasonObjectsByOutcomeId,
        selectedJourneyApplicationStep,
        journeyActionVersionsByJourneyActionId,
      },
    }: PayloadAction<PrimaryApplicationData>,
  ) => ({
    ...state,
    application,
    applicationJourneyVersion,
    documentTypes,
    entities: mergeEntities(state?.entities || [], entities),
    outcomeCustomizations,
    reviewOutcomeObjects,
    reviewReasonObjectsByOutcomeId,
    selectedJourneyApplicationStep,
    journeyActionVersionsByJourneyActionId,
  }),

  mergeSecondaryApplicationData: (
    state: State,
    {
      payload: {
        completedAlertManualReviewsByAlertManualReviewToken,
        completedCasesByCaseToken,
        entities,
        evaluationsByEvaluationToken,
        notes,
      },
    }: PayloadAction<SecondaryApplicationData>,
  ) => ({
    ...state,
    notes: mergeNotes(state?.notes || [], notes),
    evaluationsByEvaluationToken: {
      ...state?.evaluationsByEvaluationToken || {},
      ...evaluationsByEvaluationToken,
    },
    completedAlertManualReviewsByAlertManualReviewToken: {
      ...state?.completedAlertManualReviewsByAlertManualReviewToken || {},
      ...completedAlertManualReviewsByAlertManualReviewToken,
    },
    completedCasesByCaseToken: {
      ...state?.completedCasesByCaseToken || {},
      ...completedCasesByCaseToken,
    },
    entities: mergeEntities(state?.entities || [], entities),
  }),

  resetToInitialState: () => initialState,

  setCompletedCaseByCaseToken: (
    state: State,
    { payload: caseWork }: PayloadAction<CaseWork>,
  ) => ({
    ...state,
    completedCasesByCaseToken: {
      ...state?.completedCasesByCaseToken || {},
      [caseWork.caseToken]: caseWork,
    },
  }),

  setEntityData: (
    state: State,
    {
      payload: {
        id,
        notes,
      },
    }: PayloadAction<EntitySecondaryData>,
  ) => {
    const entity = (state?.entities || []).find((e: EntityPrimaryData) => e.id === id);
    const entityIndex = state?.entities?.indexOf(entity);
    const entityCopy = { ...entity };
    entityCopy.notes = notes.filter(n => !n.journey_application_id);

    return {
      ...state,
      entities: [
        ...(state?.entities?.slice(0, entityIndex) || []),
        entityCopy,
        ...(state?.entities?.slice(entityIndex + 1) || []),
      ],
      notes: notes.filter(n => n.journey_application_id),
    };
  },

  setEntityDocumentsByDocToken: (
    state: State,
    {
      payload: {
        entityDocumentsByDocToken,
        entityToken,
      },
    }: PayloadAction<{ entityDocumentsByDocToken: EntityDocumentsByDocToken; entityToken: string }>,
  ) => {
    const entity = (state?.entities || []).find((e: EntityPrimaryData) => e.token === entityToken);
    const entityIndex = state?.entities?.indexOf(entity);
    const entityCopy = { ...entity };
    entityCopy.documentsByDocToken = entityDocumentsByDocToken;

    return {
      ...state,
      entities: [
        ...(state?.entities?.slice(0, entityIndex) || []),
        entityCopy,
        ...(state?.entities?.slice(entityIndex + 1) || []),
      ],
    };
  },

  setEvaluationByEvaluationToken: (
    state: State,
    { payload: evaluation }: PayloadAction<Evaluation>,
  ) => ({
    ...state,
    evaluationsByEvaluationToken: {
      ...state?.evaluationsByEvaluationToken || {},
      [evaluation.event.evaluation_token]: evaluation,
    },
  }),

  setSelectedJourneyApplicationStep: (
    state: State,
    {
      payload: {
        alertManualReviewToken = null,
        journeyApplicationEventToken,
        entityToken,
        caseToken = null,
      },
    }: PayloadAction<SelectedJourneyApplicationStep>,
  ): State => ({
    ...state,
    selectedJourneyApplicationStep: {
      alertManualReviewToken,
      journeyApplicationEventToken,
      entityToken,
      caseToken,
    },
  }),

  setNotes: (
    state: State,
    {
      payload,
    }: PayloadAction<EntityNotes>,
  ): State => ({
    ...state,
    notes: payload,
  }),
};
